import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { DeleteModal } from "../../../../components";
import { useStore } from "../../../../helpers";
import {
  FACET_MANUAL_CONFIRM_DESC,
  FACET_MANUAL_CONFIRM_LABEL,
  FACET_MANUAL_CONFIRM_TITLE,
} from "../../tars-constants";
import { useFacetList } from "../../tars-hooks";
import AnalyticsHeader from "./AnalyticsHeader";
import AnalyticsContent from "./AnalyticsContent";
import { mapFacetListRows } from "./map-facet-list-rows";
import { useAnalyticsFacetActions } from "./useAnalyticsFacetActions";
import FacetAutogenerateConfirmModal from "./FacetAutogenerateConfirmModal";
import FacetAutogenerateLoadingOverlay from "./FacetAutogenerateLoadingOverlay";
import type { SourceFile } from "../sidebar/sources-types";
import { isFolder } from "../sidebar/sources-types";
import {
  dataBankHeaderSubtitle,
  resolveDataBankViewType,
} from "./dataBankSourceHelpers";

function AnalyticsComponentInner() {
  const { notebookId } = useParams<{ notebookId: string }>();
  const { tarsAnalyticsStore, tarsStore } = useStore();

  const parsedNotebookId = notebookId != null ? Number(notebookId) : NaN;
  const notebookIdNum = Number.isFinite(parsedNotebookId) ? parsedNotebookId : 0;
  const canvasId = tarsStore.selectedCanvas?.id ?? 0;
  const selectedSourceRaw = tarsAnalyticsStore.selectedSourceIds[0];
  const parsedSourceId = selectedSourceRaw != null ? Number(selectedSourceRaw) : NaN;
  const sourceId = Number.isFinite(parsedSourceId) ? parsedSourceId : 0;
  const facetsListEnabled = notebookIdNum > 0 && canvasId > 0;
  const { data: facetsListData } = useFacetList(
    notebookIdNum,
    sourceId,
    canvasId,
    facetsListEnabled
  );

  const { handleAddFacetManually, handleGenerateFacet, generateFacetLoading } =
    useAnalyticsFacetActions(notebookIdNum, sourceId, canvasId, selectedSourceRaw);
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false);
  const [manualConfirmOpen, setManualConfirmOpen] = useState(false);

  const selectedForHeader = tarsAnalyticsStore.selectedSourceIds[0];
  const nodeForHeader = selectedForHeader ? tarsStore.getNode(selectedForHeader) : undefined;
  const dataBankSubtitle =
    nodeForHeader &&
    !isFolder(nodeForHeader) &&
    (nodeForHeader as SourceFile).type === "dataBank"
      ? dataBankHeaderSubtitle(resolveDataBankViewType(nodeForHeader as SourceFile))
      : null;

  useEffect(() => {
    if (!facetsListEnabled) return;
    const mapped = mapFacetListRows(facetsListData, canvasId);
    tarsAnalyticsStore.setFacets(mapped);
    if (mapped.length === 0) {
      tarsAnalyticsStore.setSelectedFacetId(null);
      return;
    }
    const hasSelected = mapped.some((f) => f.id === tarsAnalyticsStore.selectedFacetId);
    if (!hasSelected) tarsAnalyticsStore.setSelectedFacetId(mapped[0].id);
  }, [
    facetsListEnabled,
    facetsListData,
    canvasId,
    tarsAnalyticsStore,
  ]);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-gray-200">
      <AnalyticsHeader
        title={tarsAnalyticsStore.analyticsTitle}
        facetCount={tarsAnalyticsStore.facetCount}
        onAddFacetManually={() => setManualConfirmOpen(true)}
        onGenerateFacet={() => setGenerateConfirmOpen(true)}
        generateFacetLoading={generateFacetLoading}
        dataBankSubtitle={dataBankSubtitle}
      />
      <FacetAutogenerateLoadingOverlay open={generateFacetLoading} />
      <AnalyticsContent />
      <DeleteModal
        isOpen={manualConfirmOpen}
        id="facet-manual-add"
        title={FACET_MANUAL_CONFIRM_TITLE}
        desc={FACET_MANUAL_CONFIRM_DESC}
        confirmLabel={FACET_MANUAL_CONFIRM_LABEL}
        onCancelClick={() => setManualConfirmOpen(false)}
        onDeleteClick={() => {
          setManualConfirmOpen(false);
          handleAddFacetManually();
        }}
      />
      <FacetAutogenerateConfirmModal
        isOpen={generateConfirmOpen}
        onCancel={() => setGenerateConfirmOpen(false)}
        onConfirm={(matrixKeys) => {
          setGenerateConfirmOpen(false);
          handleGenerateFacet(matrixKeys);
        }}
      />
    </div>
  );
}

export default observer(AnalyticsComponentInner);
